import { inject, Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable } from 'rxjs';
import { Notification } from '../models/Notification';
import { Guid } from '../../shared/utils/Guid';
import { NotificationType } from '../enums/notification-type.enum';
import { NotificationContentType } from '../enums/notification-content-type.enum';

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private _snoozing: Date | boolean;
  private readonly _snoozingSource: BehaviorSubject<Date | boolean>;
  private _notifications: Array<Notification>;
  private readonly _notificationsSource: BehaviorSubject<ReadonlyArray<Notification>>;

  /**
   * Returns whether the notification service is snoozing.
   * If the notification service is snoozing, the expiration time will be returned.
   */
  public get snoozing$(): Observable<Date | boolean> {
    return this._snoozingSource.asObservable();
  }

  public get notifications$(): Observable<ReadonlyArray<Notification>> {
    return this._notificationsSource.asObservable();
  }
  private readonly _toastrService = inject(ToastrService);

  constructor() {
    this._notifications = [];
    this._notificationsSource = new BehaviorSubject(this._notifications);

    this._snoozing = false;
    this._snoozingSource = new BehaviorSubject(this._snoozing);

    this._toastrService.toastrConfig.closeButton = true;
    this._toastrService.toastrConfig.timeOut = 5000;
    this._toastrService.toastrConfig.progressBar = true;
    this._toastrService.toastrConfig.maxOpened = 3;
    this._toastrService.toastrConfig.autoDismiss = true;
  }

  public notifyInfo(
    message: string,
    title: string = null,
    contentType: NotificationContentType = NotificationContentType.Text
  ): Notification {
    const notification = new Notification(message, title, NotificationType.Info, contentType);
    this.publishNotification(notification);

    return notification;
  }

  public notifyWarning(
    message: string,
    title: string = null,
    contentType: NotificationContentType = NotificationContentType.Text
  ): Notification {
    const notification = new Notification(message, title, NotificationType.Warning, contentType);
    this.publishNotification(notification);

    return notification;
  }

  public notifyError(
    message: string,
    title: string = null,
    contentType: NotificationContentType = NotificationContentType.Text
  ): Notification {
    const notification = new Notification(message, title, NotificationType.Error, contentType);
    this.publishNotification(notification);

    return notification;
  }

  public notifyVerbose(
    message: string,
    title: string = null,
    contentType: NotificationContentType = NotificationContentType.Text
  ): Notification {
    const notification = new Notification(message, title, NotificationType.Verbose, contentType);
    this.publishNotification(notification);

    return notification;
  }

  public snoozeNotificationsForOneHour(): void {
    const now = new Date();
    now.setHours(now.getHours() + 1);

    this._snoozing = now;
    this._snoozingSource.next(this._snoozing);
  }

  public dismissAll(): void {
    this._notifications = [];
    this._notificationsSource.next(this._notifications);
  }

  public dismissNotification(id: Guid): void {
    this._notifications = this._notifications.filter(n => n.id !== id);
    this._notificationsSource.next(this._notifications);
  }

  public enableNotifications(): void {
    this._snoozing = false;
    this._snoozingSource.next(this._snoozing);
  }

  private areNotificationsSnoozed(): boolean {
    if (this._snoozing === false) {
      return false;
    }

    const now = new Date();
    const isSnoozingActive = now < this._snoozing;

    if (!isSnoozingActive) {
      this.enableNotifications();
    }
    return isSnoozingActive;
  }

  private publishNotification(notification: Notification): void {
    this._notifications.push(notification);
    this._notificationsSource.next(this._notifications);

    if (!this.areNotificationsSnoozed()) {
      let toastrType;

      switch (notification.type) {
        case NotificationType.Verbose:
          toastrType = this._toastrService.toastrConfig.iconClasses.info;
          break;
        case NotificationType.Warning:
          toastrType = this._toastrService.toastrConfig.iconClasses.warning;
          break;
        case NotificationType.Error:
          toastrType = this._toastrService.toastrConfig.iconClasses.error;
          break;
        case NotificationType.Info:
        default:
          toastrType = this._toastrService.toastrConfig.iconClasses.success;
          break;
      }

      const toast = this._toastrService.show(
        notification.message,
        notification.title,
        {
          tapToDismiss: false,
          enableHtml: notification.contentType === NotificationContentType.Html,
        },
        toastrType
      );

      notification.registerTapSource(toast.onTap);
    }
  }
}
